import { useEffect, useState, useRef } from "react";
import Confetti from "react-confetti";
import style from "../css/MathGame.module.css";
import UAParser from "ua-parser-js";
import { ErrorHandling } from "../../../State";
import { offerEvent, updateFanduPoints } from "../../../utils/api";
import mathGame1 from "../../../icons/mathGame1.webp";
import FanduEarnBanner from "./FanduEarnBanner";
import logo from "../../../icons/logoFanduW.svg";

function MathGame({
  autoStart = false,
  onClose,
  page = "Quiz_Categories",
  isContest,
  remark,
  time,
}) {
  let mId = localStorage.getItem("mId") || "2026";

  let uuid = localStorage.getItem("uuid");

  let session_id = localStorage.getItem("session_id");

  let userId = localStorage.getItem("userId");

  const correctRef = useRef(0);
  const totalQuestionsRef = useRef(0);

  const [open, setOpen] = useState(false);

  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState("");

  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);

  const [timeLeft, setTimeLeft] = useState(time);
  const [gameOver, setGameOver] = useState(false);

  const [inputState, setInputState] = useState("");

  useEffect(() => {
    correctRef.current = correct;
  }, [correct]);

  const offerRequestData = (eventMetaData) => {
    try {
      const parser = new UAParser();

      const result = parser.getResult();

      return {
        machine_id: mId || "",
        device_id: uuid || "",
        session_id: session_id || "",
        ip_address: localStorage.getItem("ip_address") || "",
        lat: "",
        lng: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        deviceBrand: result?.device?.vendor || "Unknown",
        deviceModel: result?.device?.model || "Unknown",
        deviceType: result?.device?.type || "desktop",
        osName: result?.os?.name || "Unknown",
        osVersion: result?.os?.version || "Unknown",
        browserName: result?.browser?.name || "Unknown",
        browserVersion: result?.browser?.version || "Unknown",
        event_ts: Date.now().toString(),
        user_id: userId,
        ...eventMetaData,
      };
    } catch (error) {
      ErrorHandling("Unable to collect device information");

      return null;
    }
  };

  const avail = async (EventName, EventSubName, EventType, EventSubType) => {
    try {
      const requestData = offerRequestData({
        Event_Name: EventName,
        Event_Sub_Name: EventSubName,
        Event_Type: EventType,
        Event_Sub_Type: EventSubType,
      });

      if (!requestData) return;

      await offerEvent(requestData);
    } catch (error) {
      console.log("Offer Event Error:", error);
    }
  };

  const rewardMathGamePoints = async (correct, totalQuestions) => {
    try {
      const fanduUserId = localStorage.getItem("fanduUserId");

      if (!fanduUserId) return;

      const wrong = totalQuestions - correct;

      // Correct = 2 points
      // Wrong = 1 point
      const totalPoints = correct * 2 + wrong * 1;

      if (totalPoints <= 0) return;

      const rewardedCount =
        Number(localStorage.getItem("mathGameRewardCount")) || 0;

      // Only first 5 games are rewarded
      if (rewardedCount >= 5) {
        return;
      }

      const fanduPayload = {
        user_id: fanduUserId,
        score: totalPoints,
        is_contest: isContest ? 1 : 0,
        remark: remark ? "Math Game Contest" : "Math Game",
      };

      const response = await updateFanduPoints(fanduPayload);

      const updatedPoints = Number(response?.score) || 0;

      localStorage.setItem("userFanduPoints", updatedPoints);

      localStorage.setItem("mathGameRewardCount", rewardedCount + 1);

      if (!response?.success) {
        ErrorHandling(response.message);
      }
    } catch (error) {
      console.error("Math Game reward error:", error);
      ErrorHandling("Error updating points. Try again later.");
    }
  };

  const generateQuestion = () => {
    const operators = ["+", "-", "*"];

    const operator = operators[Math.floor(Math.random() * operators.length)];

    let a;
    let b;
    let result;

    switch (operator) {
      case "+":
        a = Math.floor(Math.random() * 20) + 1;
        b = Math.floor(Math.random() * 20) + 1;
        result = a + b;
        break;

      case "-":
        a = Math.floor(Math.random() * 30) + 10;
        b = Math.floor(Math.random() * a);
        result = a - b;
        break;

      default:
        a = Math.floor(Math.random() * 12) + 1;
        b = Math.floor(Math.random() * 12) + 1;
        result = a * b;
    }

    setQuestion({
      text: `${a} ${operator} ${b}`,
      answer: result,
    });
  };

  useEffect(() => {
    if (!answer) return;

    const timer = setTimeout(() => {
      submitAnswer();
    }, 500);

    return () => clearTimeout(timer);
  }, [answer]);

  const startGame = () => {
    setOpen(true);
    setCorrect(0);
    setWrong(0);
    totalQuestionsRef.current = 0;
    setTimeLeft(time);
    setGameOver(false);
    setAnswer("");
    generateQuestion();
    avail("Math_Game_Open", page, "Click", "Screen_Impressions");
  };

  useEffect(() => {
    if (autoStart) {
      startGame();
    }
  }, []);

  useEffect(() => {
    if (!open || gameOver) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);

          rewardMathGamePoints(correctRef.current, totalQuestionsRef.current);

          setGameOver(true);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, gameOver, correct]);

  const submitAnswer = () => {
    if (!answer.trim()) return;

    totalQuestionsRef.current += 1;

    if (Number(answer) === question.answer) {
      setCorrect((prev) => prev + 1);
      setInputState("correct");
    } else {
      setWrong((prev) => prev + 1);
      setInputState("wrong");
    }

    setTimeout(() => {
      setInputState("");
      setAnswer("");
      generateQuestion();
    }, 150);
  };

  return (
    <>
      {!autoStart && (
        <QuizCard
          onClick={startGame}
          title="Math Game"
          image={mathGame1}
          bgColor="#f9d4deff"
          buttonColor="#ef3b6a"
        />
      )}

      {open && (
        <>
          <div className={style.overlay}>
            <div className={style.card}>
              <div
                style={{
                  backgroundColor: "#1143b8",
                  borderRadius: "24px 24px 0px 0px",
                }}
              >
                <img
                  src={logo}
                  style={{ width: "120px", padding: "10px 20px" }}
                />
              </div>
              {!gameOver ? (
                <>
                  <div className={style.header}>
                    <div
                      className={`${style.timer} ${
                        timeLeft <= 5 ? style.timerDanger : ""
                      }`}
                    >
                      Time Left : {timeLeft}s
                    </div>
                  </div>

                  <div
                    className={style.progress}
                    style={{
                      width: `${(timeLeft / time) * 100}%`,
                    }}
                  />

                  <div key={question?.text} className={style.question}>
                    {question?.text} = ?
                  </div>

                  <div style={{ padding: "0px 24px 20px 24px" }}>
                    {" "}
                    <input
                      autoFocus
                      type="number"
                      value={answer}
                      placeholder="Enter Answer"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={`${style.input}
                    ${inputState === "correct" ? style.correctInput : ""}
                    ${inputState === "wrong" ? style.wrongInput : ""}`}
                      onChange={(e) => setAnswer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          submitAnswer();
                        }
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  {correct > 3 && (
                    <Confetti recycle={false} numberOfPieces={180} />
                  )}

                  <p className={style.resultTitle}>🎉 Time's Up!</p>

                  <div className={style.finalScore}>
                    {correct * 2 + (totalQuestionsRef.current - correct)} FP
                  </div>
                  <p className={style.finalText}>Fandu Points Earned</p>
                  <div className={style.bestScore}>Your Score: {correct}</div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      padding: "24px",
                    }}
                  >
                    <button
                      className={style.submitBtn}
                      onClick={() => {
                        if (autoStart) {
                          onClose();
                        } else {
                          setOpen(false);
                          avail(
                            "Math_Game_Close",
                            "Quiz_Categories",
                            "Click",
                            "Screen_Impressions",
                          );
                        }
                      }}
                    >
                      Close
                    </button>
                    <button
                      className={style.submitBtn}
                      style={{
                        background:
                          "linear-gradient(to bottom, #FF9130 0%, #EC7000 100%)",
                      }}
                      onClick={() => {
                        startGame();
                        avail(
                          "Math_Game_Restart",
                          "Quiz_Categories",
                          "Click",
                          "Screen_Impressions",
                        );
                      }}
                    >
                      Play Again
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default MathGame;

const QuizCard = ({ onClick, title, image, bgColor, buttonColor, avail }) => {
  return (
    <div
      className={style.quizCard}
      style={{
        background: `linear-gradient(to bottom, #FFFFFF, ${bgColor})`,
        border: `0.5px solid #EEEEEE`,
      }}
      onClick={() => onClick()}
    >
      <p className={style.quizTitle}>{title}</p>

      <div className={style.quizImageWrapper}>
        <img src={image} alt={title} className={style.quizImage} />
      </div>

      <div
        // type="button"
        className={style.quizButton}
        // style={{ backgroundColor: buttonColor }}
      >
        <FanduEarnBanner
          topText="Play &"
          bottomText="Earn"
          number={50}
          background={buttonColor}
          topTextColor="#FFFFFF"
          borderColor="#FAD140"
          bottomTextColor="#FAD140"
        />
      </div>
    </div>
  );
};
